import { useState, useEffect, useCallback } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { FormattedMessage, FormattedDate } from 'react-intl'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import IconButton from '@mui/material/IconButton'
import RefreshIcon from '@mui/icons-material/Refresh'
import Tooltip from '@mui/material/Tooltip'
import { useTeam } from '../../contexts/TeamContext'
import { useNodes } from '../../contexts/NodesContext'
import { useProjects } from '../../contexts/ProjectsContext'
import { useServices } from '../../contexts/ServicesContext'
import useNodeTasksResources from '../../shared/resources/api/v1/admin/useNodeTasksResources'
import LoadingState from '../../shared/components/LoadingState'
import ErrorState from '../../shared/components/ErrorState'
import Page from '../../shared/components/layouts/Page'
import PageHeader from '../../shared/components/layouts/PageHeader'
import PageBody from '../../shared/components/layouts/PageBody'
import TaskFilters from './components/TaskFilters'

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'pending':
    case 'running':
      return 'warning'
    case 'failed':
      return 'error'
    default:
      return 'default'
  }
}

const INITIAL_FILTERS = {
  status: '',
  taskType: '',
  nodeId: '',
  projectId: '',
  serviceId: '',
  dateFrom: null,
  dateTo: null,
}

function TaskIndexPage() {
  const { teamSlug } = useParams()
  const { team } = useTeam()
  const { nodesById, loading: nodesLoading } = useNodes()
  const { projectsById, loading: projectsLoading } = useProjects()
  const { servicesById, loading: servicesLoading } = useServices()
  const { all } = useNodeTasksResources(teamSlug)

  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)

  const loadTasks = useCallback(() => {
    setLoading(true)
    setError(null)

    const params = {}
    if (filters.status) params.status = filters.status
    if (filters.taskType) params.task_type = filters.taskType
    if (filters.nodeId) params.node_ids = [filters.nodeId]
    if (filters.projectId) params.project_ids = [filters.projectId]
    if (filters.serviceId) params.service_ids = [filters.serviceId]
    if (filters.dateFrom) params.created_after = filters.dateFrom.toISOString()
    if (filters.dateTo) params.created_before = filters.dateTo.endOf('day').toISOString()
    params.limit = rowsPerPage
    params.offset = page * rowsPerPage

    all({ params })
      .then((response) => {
        setTasks(response.nodeTasks || [])
        setTotal(response.meta?.total || 0)
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamSlug, filters, page, rowsPerPage])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters)
    setPage(0)
  }

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS)
    setPage(0)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const contextLoading = nodesLoading || projectsLoading || servicesLoading

  if (contextLoading) return <LoadingState />
  if (error) return <ErrorState messageId="tasks.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="tasks.title" />}
        entities={{ team }}
        actions={
          <Tooltip title={<FormattedMessage id="tasks.filters.clear" />}>
            <IconButton onClick={loadTasks} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />
      <PageBody>
        <TaskFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
        />

        {loading ? (
          <LoadingState />
        ) : tasks.length === 0 ? (
          <Typography color="text.secondary">
            <FormattedMessage id="tasks.empty" />
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.status" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.type" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.node" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.project" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.service" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.error" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="tasks.index.columns.createdAt" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => {
                  const node = nodesById[task.nodeId]
                  const project = projectsById[task.dockerProjectId]
                  const service = servicesById[task.dockerServiceId]

                  return (
                    <TableRow key={task.id}>
                      <TableCell>
                        <Chip
                          label={<FormattedMessage id={`tasks.status.${task.status}`} />}
                          color={getStatusColor(task.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <FormattedMessage id={`tasks.type.${task.taskType}`} />
                      </TableCell>
                      <TableCell>
                        {node ? (
                          <Link
                            component={RouterLink}
                            to={`/teams/${teamSlug}/nodes/${node.id}`}
                          >
                            {node.name}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {project ? (
                          <Link
                            component={RouterLink}
                            to={`/teams/${teamSlug}/docker/projects/${project.slug}`}
                          >
                            {project.name}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {service && project ? (
                          <Link
                            component={RouterLink}
                            to={`/teams/${teamSlug}/docker/projects/${project.slug}/services/${service.slug}`}
                          >
                            {service.name}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {task.error ? (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {task.error}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <FormattedDate
                          value={task.createdAt}
                          year="numeric"
                          month="short"
                          day="numeric"
                          hour="numeric"
                          minute="numeric"
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && tasks.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </PageBody>
    </Page>
  )
}

export default TaskIndexPage
